import { useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import PrintIcon from "@mui/icons-material/PrintOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import { QRCodeSVG } from "qrcode.react";
import Swal from "sweetalert2";

import PageHeader from "components/PageHeader";
import { useAuth } from "contexts/AuthContext";

function RegistrationQrCode() {
  const { church } = useAuth();
  const [copied, setCopied] = useState(false);

  const frontendOrigin =
    process.env.REACT_APP_FRONTEND_URL ||
    (window.location.hostname.includes("api.")
      ? window.location.origin.replace("api.", "hojci.")
      : window.location.origin);

  const registrationUrl = church?.id ? `${frontendOrigin}/member-registration/${church.id}` : null;

  const handleCopy = async () => {
    if (!registrationUrl) return;
    try {
      await navigator.clipboard.writeText(registrationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      Swal.fire({ title: "Could not copy link", icon: "error" });
    }
  };

  const handlePrint = () => window.print();

  if (!church?.id) {
    return <Typography sx={{ py: 6 }}>Loading church details...</Typography>;
  }

  return (
    <Box>
      <PageHeader
        title="Registration QR Code"
        subtitle="A unique QR code for your church. Print it and display it at the entrance — new visitors scan it to fill out the membership form themselves and get their login sent straight to their phone."
      />

      <Card>
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" fontWeight={700} mb={0.5}>
            {church.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={3}>
            Scan to register as a new member
          </Typography>

          <Box
            sx={{
              display: "inline-block",
              p: 3,
              bgcolor: "#fff",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <QRCodeSVG value={registrationUrl} size={240} />
          </Box>

          <Box sx={{ maxWidth: 480, mx: "auto", mt: 3 }}>
            <TextField
              fullWidth
              size="small"
              value={registrationUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopy} edge="end" size="small">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {copied && (
              <Typography variant="caption" color="success.main" display="block" mt={0.5}>
                Link copied to clipboard.
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ mt: 3 }}
          >
            Print QR Poster
          </Button>
        </Box>
      </Card>
    </Box>
  );
}

export default RegistrationQrCode;
